import React, {FC, memo} from 'react';

import {VisualCellActivityTypesEnum} from '../../../../../../libs/inboxes/business/src/cells/cellActivity';
import {assertUnreachable} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {MessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {UnreadStatusesEnum} from '../../../../../helpers/models/conversationHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivityTypesEnum,
} from '../../../../../models/api/activityModel';
import {ConversationHighlightModel} from '../../../../../models/api/conversationHighlightModel';
import {DraftSummaryModel} from '../../../../../models/api/draftSummaryModel';
import {isMacroSource} from '../../../../../store/data/activities/activitiesCollapseSelectors';
import {
  BumpCommentModel,
  CommentDraftSummary,
  LastCommentDraftModel,
  LastCommentModel,
} from '../../../../../store/data/conversations/conversationCellSelectors';
import {findMostRecentActivityForCell} from './cellActivityCommon';
import {CellComment, renderSummary} from './cellComment';
import {CellCommentDraftSummary} from './cellCommentDraftSummary';
import {CellCommentingPrecog} from './cellCommentingPrecog';
import {CellCommentLine, CellCommentLineProps} from './cellCommentLine';
import {CellConversationShared} from './cellConversationShared';
import {CellDraft} from './cellDraft';
import {CellReplyingPrecog} from './cellReplyingPrecog';
import {CellRuleNotify} from './cellRuleNotify';
import {
  CellSearchActivitySummaryHighlight,
  VisualSearchActivityModel,
} from './cellSearchActivitySummaryHighlight';
import {CellSummary} from './cellSummary';

/*
 * Props.
 */

interface CellActivityProps {
  mentionableAliases?: ReadonlySet<string>;
  summary: string;
  commentingTeammate?: TeammateModel;
  replyingTeammate?: TeammateModel;
  draftSeedAuthors?: ReadonlyArray<TeammateModel>;
  lastComment: LastCommentModel | undefined;
  lastCommentDraft: LastCommentDraftModel | undefined;
  lastManualMessage: MessageModel | undefined;
  isSelected?: boolean;
  draft: DraftSummaryModel | undefined;
  commentDraftSummary: CommentDraftSummary | undefined;
  bumpActivity?: ActivityModel;
  bumpComment?: BumpCommentModel;
  bumpTeammateSource?: TeammateModel;
  unreadStatus?: UnreadStatusesEnum;
  isInDraftsFolder: boolean;
  /** User ID or delegated teammate ID. */
  teammateId: number;
  isCompact?: boolean;

  // Search
  conversationHighlight?: ConversationHighlightModel;
  searchActivity?: VisualSearchActivityModel;
  searchResultCommentSummary?: VisualSearchResultCommentSummary;
  conversationId?: number;
}

export type VisualSearchResultCommentSummary = Pick<CellCommentLineProps, 'text' | 'author'>;

/*
 * Component.
 */

export const CellActivity: FC<CellActivityProps> = memo(
  (props) =>
    maybeRenderPrecog(props) ??
    maybeRenderSearchHighlight(props) ??
    maybeRenderBumpActivity(props) ??
    maybeRenderMostRecentActivity(props) ??
    maybeRenderSearchResultComment(props) ??
    renderConversationSummary(props),
);

function maybeRenderPrecog(props: CellActivityProps) {
  const {isSelected} = props;

  // The replying precog always takes precedence.
  if (props.replyingTeammate) {
    return <CellReplyingPrecog author={props.replyingTeammate} isSelected={isSelected} />;
  }

  // After that comes the commenting precog.
  if (props.commentingTeammate) {
    return <CellCommentingPrecog author={props.commentingTeammate} isSelected={isSelected} />;
  }

  return null;
}

function maybeRenderSearchHighlight(props: CellActivityProps) {
  const {isSelected, searchActivity, conversationHighlight, mentionableAliases} = props;

  // If we are in search mode show the highlighted summary.
  if (conversationHighlight?.summary) {
    return (
      <CellSearchActivitySummaryHighlight
        isSelected={isSelected}
        summaryHighlight={conversationHighlight.summary}
        searchActivity={searchActivity}
        mentionableAliases={mentionableAliases}
      />
    );
  }

  // If we are in search mode show the attachment's filename.
  if (conversationHighlight?.attachments) {
    return (
      <CellSummary
        isSelected={isSelected}
        summary={conversationHighlight?.attachments.text}
        highlight={conversationHighlight.attachments}
        type={searchActivity?.visualType}
        attachmentContentType={searchActivity?.visualContentType}
      />
    );
  }

  return null;
}

function maybeRenderBumpActivity(props: CellActivityProps) {
  const {bumpActivity, bumpComment, bumpTeammateSource, isSelected, unreadStatus, mentionableAliases} = props;

  // If we have a bump comment, render it.
  if (bumpComment) {
    return (
      <CellComment
        comment={bumpComment.comment}
        author={bumpComment.author}
        hasFollowingComments={!bumpComment.isLastComment}
        mentionableAliases={mentionableAliases}
        isSelected={isSelected}
        isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
        isCompact={props.isCompact}
      />
    );
  }

  // If we have a basic bump activity, render it.
  if (bumpActivity && bumpActivity.visualType === ActivityVisualTypesEnum.BASIC) {
    // If we have a tracker_share, render it
    if (bumpActivity.type === BasicActivityTypesEnum.TRACKER_SHARE && bumpTeammateSource) {
      return <CellConversationShared source={bumpTeammateSource} isSelected={isSelected} />;
    }

    // If we have a notification from a rule, render it.
    if (bumpActivity.type === BasicActivityTypesEnum.RULE_NOTIFY) {
      return <CellRuleNotify isSelected={isSelected} isMacro={isMacroSource(bumpActivity.source)} />;
    }
  }

  return null;
}

function maybeRenderMostRecentActivity(props: CellActivityProps) {
  const {isSelected, unreadStatus, mentionableAliases, isCompact} = props;
  const mostRecentActivity = findMostRecentActivityForCell(props);

  switch (mostRecentActivity?.type) {
    case VisualCellActivityTypesEnum.TIMELINE_COMMENT: {
      return (
        <CellComment
          author={mostRecentActivity.author}
          comment={mostRecentActivity.comment}
          mentionableAliases={mentionableAliases}
          isSelected={isSelected}
          isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
          isCompact={isCompact}
        />
      );
    }

    case VisualCellActivityTypesEnum.TIMELINE_MESSAGE: {
      const {isReply, blurb} = mostRecentActivity.message;
      return <CellSummary isSelected={isSelected} isReply={isReply} summary={blurb} />;
    }

    case VisualCellActivityTypesEnum.DRAFT_MESSAGE: {
      return (
        <CellDraft
          isInDraftsFolder={props.isInDraftsFolder}
          draft={mostRecentActivity.draft}
          isSelected={isSelected}
          conversationId={props.conversationId}
        />
      );
    }

    case VisualCellActivityTypesEnum.DRAFT_COMMENT: {
      // TODO: Render the comment summary and full comment cases the same.
      if (`blurb` in mostRecentActivity.draft) {
        return (
          <CellCommentDraftSummary
            summary={mostRecentActivity.draft}
            author={mostRecentActivity.author}
            mentionableAliases={mentionableAliases}
            isSelected={isSelected}
            isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
            isCompact={isCompact}
          />
        );
      }

      return (
        <CellComment
          comment={mostRecentActivity.draft}
          author={mostRecentActivity.author}
          mentionableAliases={mentionableAliases}
          isSelected={isSelected}
          isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
          isCompact={isCompact}
        />
      );
    }

    case undefined:
      return null;

    default:
      assertUnreachable(mostRecentActivity);
      return null;
  }
}

function maybeRenderSearchResultComment(props: CellActivityProps) {
  const {isSelected, searchResultCommentSummary, mentionableAliases} = props;

  if (searchResultCommentSummary) {
    return (
      <CellCommentLine
        isSelected={isSelected}
        text={searchResultCommentSummary.text}
        author={searchResultCommentSummary.author}
        annotation={undefined}
        renderSummary={renderSummary}
        shouldDisplayAttachmentIcon={false}
        shouldUseEllipsisOverflow={false}
        mentionableAliases={mentionableAliases}
      />
    );
  }

  return null;
}

function renderConversationSummary(props: CellActivityProps) {
  const {isSelected} = props;

  // Default to rendering the conversation summary.
  return <CellSummary isSelected={isSelected} summary={props.summary} />;
}
